import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { restaurantTableGroupApi, restaurantTableApi, RestaurantTableGroupDto, RestaurantTableDto } from '../../../../services/restaurantApiService';
import { useTranslation } from 'react-i18next';

interface RestaurantTableGroupsTabProps {
  restaurantScreenId: number;
  companyId: string;
}

const RestaurantTableGroupsTab: React.FC<RestaurantTableGroupsTabProps> = ({
  restaurantScreenId,
  companyId
}) => {
  const { t } = useTranslation('salesSystem');
  const [groupName, setGroupName] = useState('');
  const [groups, setGroups] = useState<RestaurantTableGroupDto[]>([]);
  const [allTables, setAllTables] = useState<RestaurantTableDto[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<RestaurantTableGroupDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [groupToDelete, setGroupToDelete] = useState<RestaurantTableGroupDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isEditMode = !!selectedGroup;

  const fetchGroups = async () => {
    if (!restaurantScreenId) return;
    setLoading(true);
    setListError(null);
    try {
      const groupsData = await restaurantTableGroupApi.getByRestaurantScreenId(restaurantScreenId);
      setGroups(groupsData.sort((a, b) => a.Name.localeCompare(b.Name)));
      
      const tablesData = await restaurantTableApi.getByRestaurantScreenId(restaurantScreenId);
      setAllTables(tablesData);
    } catch (err) {
      console.error("❌ Masa grupları yüklenirken hata:", err);
      setListError(t('restaurantTableGroupsTab.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [restaurantScreenId]);

  const resetForm = () => {
    setGroupName('');
    setSelectedGroup(null);
    setError(null);
  };

  const handleSelectGroupForEdit = (group: RestaurantTableGroupDto) => {
    setSelectedGroup(group);
    setGroupName(group.Name);
    setError(null);
  };

  const handleSubmit = async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      setError(t('restaurantTableGroupsTab.errors.groupNameRequired'));
      return;
    }

    const conflictingGroup = groups.find(
      g => g.Name.toLowerCase() === trimmedName.toLowerCase() && g.Id !== selectedGroup?.Id
    );

    if (conflictingGroup) {
      setError(`'${trimmedName}' adında bir grup zaten mevcut.`);
      return;
    }

    setFormLoading(true);
    setError(null);

    try {
      if (isEditMode && selectedGroup) {
        await restaurantTableGroupApi.update(selectedGroup.Id, { 
          Id: selectedGroup.Id,
          Name: trimmedName 
        });
        console.log(`✅ Grup güncellendi: ${trimmedName}`);
      } else {
        await restaurantTableGroupApi.create({
          RestaurantScreenId: restaurantScreenId,
          Name: trimmedName,
          DisplayOrder: groups.length
        });
        console.log(`✅ Yeni grup eklendi: ${trimmedName}`);
      }
      
      await fetchGroups();
      resetForm();
    } catch (err: any) {
      console.error("❌ Masa grubu kaydedilirken hata:", err);
      setError(err.response?.data?.Message || err.message || t('restaurantTableGroupsTab.errors.saveFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  const openDeleteConfirmation = (group: RestaurantTableGroupDto) => {
    const isGroupInUse = allTables.some(table => table.TableGroupId === group.Id);
    if (isGroupInUse) {
      const tableNames = allTables
        .filter(t => t.TableGroupId === group.Id)
        .map(t => t.Name)
        .join(', ');
      setDeleteError(
        t('restaurantTableGroupsTab.delete.inUseByTables', { groupName: group.Name, tableNames })
      );
    } else {
      setDeleteError(null);
    }
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setGroupToDelete(null);
    setDeleteError(null);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    if (deleteError && deleteError.includes("kullanılıyor")) {
      return;
    }

    setFormLoading(true);
    try {
      await restaurantTableGroupApi.delete(groupToDelete.Id);
      console.log(`✅ Grup silindi: ${groupToDelete.Name}`);
      
      await fetchGroups();
      handleCloseDeleteConfirmation();
      
      if (selectedGroup?.Id === groupToDelete.Id) {
        resetForm();
      }
    } catch (err: any) {
      console.error("❌ Masa grubu silinirken hata:", err);
      setDeleteError(err.response?.data?.Message || err.message || t('restaurantTableGroupsTab.errors.deleteFailed'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', maxHeight: 450 }}>
            <Typography variant="h6" gutterBottom>
              {isEditMode ? t('restaurantTableGroupsTab.form.editTitle') : t('restaurantTableGroupsTab.form.newTitle')}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              autoFocus={!isEditMode}
              margin="dense"
              label={t('restaurantTableGroupsTab.form.groupName')}
              type="text"
              fullWidth
              variant="outlined"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              disabled={formLoading}
              sx={{ mb: 2 }}
              onKeyPress={(ev) => {
                if (ev.key === 'Enter' && !formLoading && groupName.trim()) {
                  handleSubmit();
                  ev.preventDefault();
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              {isEditMode && (
                <Button onClick={resetForm} disabled={formLoading} color="inherit" size="small">
                  {t('restaurantTableGroupsTab.form.clearNew')}
                </Button>
              )}
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={formLoading || !groupName.trim()}
                sx={{ ml: isEditMode ? 1 : 'auto' }}
                size="small"
              >
                {formLoading ? <CircularProgress size={20} color="inherit" /> : (isEditMode ? t('restaurantTableGroupsTab.form.update') : t('restaurantTableGroupsTab.form.add'))}
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%', maxHeight: 450, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>{t('restaurantTableGroupsTab.list.title')}</Typography>
            {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                <CircularProgress /> <Typography sx={{ ml: 1 }}>{t('restaurantTableGroupsTab.list.loading')}</Typography>
              </Box>
            )}
            {!loading && !listError && groups.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, flexDirection: 'column' }}>
                <Typography color="text.secondary">{t('restaurantTableGroupsTab.list.emptyTitle')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('restaurantTableGroupsTab.list.emptyHint')}</Typography>
              </Box>
            )}
            {!loading && !listError && groups.length > 0 && (
              <List dense sx={{ overflowY: 'auto', flexGrow: 1, maxHeight: 350 }}>
                {groups.map((group) => (
                  <ListItem
                    key={group.Id}
                    divider
                    selected={selectedGroup?.Id === group.Id}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      bgcolor: selectedGroup?.Id === group.Id ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: selectedGroup?.Id !== group.Id ? 'action.hover' : 'action.selected'
                      }
                    }}
                  >
                    <ListItemText primary={group.Name} />
                    <ListItemSecondaryAction>
                      <Tooltip title={t('restaurantTableGroupsTab.list.edit')}>
                        <IconButton edge="end" size="small" onClick={() => handleSelectGroupForEdit(group)} disabled={formLoading}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('restaurantTableGroupsTab.list.delete')}>
                        <IconButton edge="end" size="small" onClick={() => openDeleteConfirmation(group)} disabled={formLoading} color="error" sx={{ ml: 0.5 }}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {groupToDelete && (
        <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteConfirmation} maxWidth="xs">
          <DialogTitle>{t('restaurantTableGroupsTab.delete.title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('restaurantTableGroupsTab.delete.description', { name: groupToDelete.Name })}
            </DialogContentText>
            {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirmation} disabled={formLoading}>{t('restaurantTableGroupsTab.common.cancel')}</Button>
            <Button
              onClick={handleDeleteGroup}
              color="error"
              variant="contained"
              disabled={formLoading || (!!deleteError && deleteError.includes("kullanılıyor"))}
              startIcon={formLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {formLoading ? t('restaurantTableGroupsTab.delete.deleting') : t('restaurantTableGroupsTab.list.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default RestaurantTableGroupsTab;
